import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createSlider,
  getSliders,
  getSliderById,
  updateSlider,
  deleteSlider,
} from '@app/api/sliders.api';
import { Slider, CreateSliderDto, UpdateSliderDto, SliderFilters, SliderPaginatedResponse } from '@app/types/api.types';

export interface SlidersState {
  items: Slider[];
  currentItem: Slider | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: SliderFilters;
  loading: boolean;
  error: string | null;
}

const initialState: SlidersState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchSliders = createAsyncThunk(
  'sliders/fetchSliders',
  async (filters: SliderFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await getSliders(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch sliders');
    }
  }
);

export const fetchSliderById = createAsyncThunk(
  'sliders/fetchSliderById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getSliderById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch slider');
    }
  }
);

export const createSliderAction = createAsyncThunk(
  'sliders/createSlider',
  async (data: CreateSliderDto, { rejectWithValue }) => {
    try {
      const response = await createSlider(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create slider');
    }
  }
);

export const updateSliderAction = createAsyncThunk(
  'sliders/updateSlider',
  async ({ id, data }: { id: string; data: UpdateSliderDto }, { rejectWithValue }) => {
    try {
      const response = await updateSlider(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update slider');
    }
  }
);

export const deleteSliderAction = createAsyncThunk(
  'sliders/deleteSlider',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteSlider(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete slider');
    }
  }
);

const slidersSlice = createSlice({
  name: 'sliders',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<SliderFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Sliders
    builder.addCase(fetchSliders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSliders.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit),
        hasNext: action.payload.hasNext || action.payload.page < (action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit)),
        hasPrev: action.payload.hasPrev || action.payload.page > 1,
      };
    });
    builder.addCase(fetchSliders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Slider By ID
    builder.addCase(fetchSliderById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSliderById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchSliderById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Slider
    builder.addCase(createSliderAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSliderAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(createSliderAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Slider
    builder.addCase(updateSliderAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateSliderAction.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateSliderAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Slider
    builder.addCase(deleteSliderAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteSliderAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteSliderAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem } = slidersSlice.actions;
export default slidersSlice.reducer;

